import { createSlice } from "@reduxjs/toolkit";
import { addSentryBreadcrumb } from "utils/sentry";

import {
  clearFeatureFlagOverride,
  fetchFeatureFlagDetails,
  fetchFeatureFlags,
  updateFeatureFlagOverride,
} from "./actions";

const initialState = {
  flags: {},
  loaded: false,
  loading: false,
  error: null,
  details: [],
  detailsLoading: false,
  detailsError: null,
};

const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    setFlags: (state, action) => {
      state.flags = action.payload;
      state.loaded = true;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setDetails: (state, action) => {
      state.details = action.payload;
      state.detailsError = null;
    },
    setDetailsLoading: (state, action) => {
      state.detailsLoading = action.payload;
    },
    setDetailsError: (state, action) => {
      state.detailsError = action.payload;
    },
    upsertDetail: (state, action) => {
      const detail = action.payload;
      const idx = state.details.findIndex(
        (d) => d.publicName === detail.publicName,
      );
      if (idx >= 0) {
        state.details[idx] = detail;
      } else {
        state.details.push(detail);
      }
      state.flags[detail.publicName] = detail.resolved;
    },
  },
});

export const {
  setFlags,
  setLoading,
  setError,
  setDetails,
  setDetailsLoading,
  setDetailsError,
  upsertDetail,
} = featureFlagsSlice.actions;

export const loadFeatureFlags = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const flags = await fetchFeatureFlags();
    dispatch(setFlags(flags));
  } catch (error) {
    addSentryBreadcrumb({
      category: "feature-flags",
      level: "warning",
      message: "Failed to load feature flags; degrading to defaults",
      data: { error: error?.message },
    });
    dispatch(setError(error?.message ?? "Failed to load feature flags"));
  } finally {
    dispatch(setLoading(false));
  }
};

export const loadFeatureFlagDetails = () => async (dispatch) => {
  dispatch(setDetailsLoading(true));
  try {
    const details = await fetchFeatureFlagDetails();
    dispatch(setDetails(details));
  } catch (error) {
    dispatch(
      setDetailsError(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to load feature flag details",
      ),
    );
  } finally {
    dispatch(setDetailsLoading(false));
  }
};

export const setFeatureFlagOverride =
  (publicName, enabled) => async (dispatch) => {
    const detail = await updateFeatureFlagOverride(publicName, enabled);
    dispatch(upsertDetail(detail));
    return detail;
  };

export const clearFeatureFlagOverrideThunk =
  (publicName) => async (dispatch) => {
    const detail = await clearFeatureFlagOverride(publicName);
    dispatch(upsertDetail(detail));
    return detail;
  };

export default featureFlagsSlice.reducer;
